'use client'

import { SxProps } from '@mui/material'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import { DatePicker, DateView } from '@mui/x-date-pickers'
import { Dayjs } from 'dayjs'
import {
  ComponentProps,
  CSSProperties,
  forwardRef,
  Ref,
  useEffect,
  useState,
} from 'react'

import { Icons } from 'variables'

type ExcludeProps = 'onChange'
type DatePickerProps = Omit<
  ComponentProps<typeof DatePicker<Dayjs | null>>,
  ExcludeProps
>

interface Props extends DatePickerProps {
  name: string
  value?: Dayjs | null
  format?: string
  onChange?: (date: Dayjs | null) => void
  openTo?: DateView
  views?: DateView[]
  canDelete?: boolean
  showArrowButton?: boolean
}

const Move = {
  PREV: 0,
  NEXT: 1,
} as const
type MoveType = (typeof Move)[keyof typeof Move]

export default forwardRef(DatePickerForm)
function DatePickerForm(props: Props, ref: Ref<HTMLDivElement>) {
  const propsWithDefault: Props = {
    format: props.format ?? 'YYYY/MM/DD (dd)',
    openTo: props.openTo ?? 'day',
    views: props.views ?? ['year', 'month', 'day'],
    canDelete: props.canDelete ?? false,
    showArrowButton: props.showArrowButton ?? false,
    ...props,
  }

  const [selected, setSelected] = useState<Dayjs | null>(
    propsWithDefault.value ?? null,
  )

  useEffect(() => {
    propsWithDefault.onChange?.(selected)
  }, [selected])

  const handleClickMove = (direction: MoveType) => () => {
    if (selected === null) {
      return
    }

    setSelected(
      direction === Move.PREV
        ? selected.subtract(1, propsWithDefault.openTo)
        : selected.add(1, propsWithDefault.openTo),
    )
  }

  return (
    <Box style={containerStyle}>
      {propsWithDefault.showArrowButton && (
        <IconButton
          style={moveButtonStyle}
          onClick={handleClickMove(Move.PREV)}
        >
          <Icons.leftButton style={moveIconStyle} />
        </IconButton>
      )}
      <DatePicker
        {...propsWithDefault}
        sx={
          {
            ...inputStyle,
            ...propsWithDefault.sx,
          } as SxProps
        }
        ref={ref}
        value={selected}
        className="datePicker"
        slotProps={
          propsWithDefault.canDelete
            ? {
                field: { clearable: true },
              }
            : {}
        }
      />
      {propsWithDefault.showArrowButton && (
        <IconButton
          style={moveButtonStyle}
          onClick={handleClickMove(Move.NEXT)}
        >
          <Icons.rightButton style={moveIconStyle} />
        </IconButton>
      )}
    </Box>
  )
}

const inputStyle: SxProps = {
  height: '100%',
  '& .MuiInputBase-root': {
    height: 'inherit',
  },
}

const containerStyle: CSSProperties = {
  display: 'flex',
  gap: '8px',
  height: '30px',
}

const moveButtonStyle: CSSProperties = {
  backgroundColor: '#AFB6B6',
  borderRadius: '4px',
  padding: '4px',
  height: '100%',
  aspectRatio: '1',
}

const moveIconStyle: CSSProperties = {
  color: 'white',
}
